import { calcAgeFromDob } from '../utils/caseFieldValidation';
import type {
  ApplicableAreaRow,
  CriminalSaveStep1,
  CriminalSaveStep2,
  CriminalSaveStep3,
  CriminalSaveStep3SuspectName,
  CriminalSaveStep4,
  CriminalSaveStep5,
  CriminalSaveStep5File,
  CriminalStepsResponse,
  PhysicalFileDeliveryMethodRow,
  SaveCriminalStepsPayload,
} from '../types/criminalApi';
import { FILE_CATEGORY_CRIMINAL } from '../mocks/criminalMasterData';
import { isOtherLookup } from '../pages/wizard/criminal/criminalConstants';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import type { AccusedRecord, CriminalWizardDraft } from '../types/criminalCaseRequest';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';

export type CriminalWizardStep = 1 | 2 | 3 | 4 | 5;

function step2HasAnyData(s2: CriminalSaveStep2 | undefined): boolean {
  return Boolean(
    (s2?.name_of_victim ?? '').trim() ||
      (s2?.date_of_birth ?? '').trim() ||
      (s2?.gender_id ?? '').trim(),
  );
}

function suspectNameEntryHasData(entry: string | CriminalSaveStep3SuspectName | undefined): boolean {
  if (typeof entry === 'string') return Boolean(entry.trim());
  if (!entry || typeof entry !== 'object') return false;
  return Boolean((entry.suspect_name ?? '').trim());
}

function step3HasAnyData(s3: CriminalSaveStep3 | undefined): boolean {
  const suspects = s3?.suspects;
  const suspectNames = s3?.suspect_names;
  return Boolean(
    (s3?.suspect_name ?? '').trim() ||
      (Array.isArray(suspects) && suspects.length > 0) ||
      (Array.isArray(suspectNames) && suspectNames.some(suspectNameEntryHasData)) ||
      (s3?.suspect_status_id ?? '').trim() ||
      (s3?.date_of_offence_from ?? '').trim() ||
      (s3?.place_of_offence ?? '').trim() ||
      (s3?.urgency_level_id ?? '').trim(),
  );
}

function step4HasAnyData(s4: CriminalSaveStep4 | undefined): boolean {
  return Boolean((s4?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step5HasAnyData(s5: CriminalSaveStep5 | undefined): boolean {
  const files = (s5?.uploaded_files?.length ? s5.uploaded_files : s5?.documents) ?? [];
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

function step1LooksComplete(s1: CriminalSaveStep1 | undefined): boolean {
  return Boolean(
    (s1?.sub_category_id ?? '').trim() &&
      (s1?.description ?? '').trim() &&
      (s1?.case_source_id ?? '').trim() &&
      (s1?.province_id ?? '').trim() &&
      (s1?.external_case_reference_number ?? '').trim(),
  );
}

function step2LooksComplete(s2: CriminalSaveStep2 | undefined): boolean {
  return Boolean((s2?.name_of_victim ?? '').trim() && (s2?.gender_id ?? '').trim());
}

function step3LooksComplete(s3: CriminalSaveStep3 | undefined): boolean {
  const urgencyOk = Boolean((s3?.urgency_level_id ?? '').trim());
  if (s3?.accused_details_available === false) return urgencyOk;

  const suspects = s3?.suspects;
  const hasSuspect =
    Boolean((s3?.suspect_name ?? '').trim()) ||
    (Array.isArray(suspects) &&
      suspects.some((s) => {
        if (typeof s === 'string') return s.trim().length > 0;
        if (!s || typeof s !== 'object') return false;
        const row = s as Record<string, unknown>;
        return Boolean(
          (row.suspect_name ?? row.name ?? row.accused_name ?? '').toString().trim(),
        );
      })) ||
    (s3?.suspect_names ?? []).some(suspectNameEntryHasData);
  return Boolean(hasSuspect && urgencyOk);
}

function step4LooksComplete(s4: CriminalSaveStep4 | undefined): boolean {
  return Boolean((s4?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step5LooksComplete(s5: CriminalSaveStep5 | undefined): boolean {
  return step5HasAnyData(s5);
}

function latestSavedCriminalStep(
  response: Pick<CriminalStepsResponse, 'step_1' | 'step_2' | 'step_3' | 'step_4' | 'step_5'>,
): CriminalWizardStep {
  if (step5HasAnyData(response.step_5)) return 5;
  if (step4HasAnyData(response.step_4)) return 4;
  if (step3HasAnyData(response.step_3)) return 3;
  if (step2HasAnyData(response.step_2)) return 2;
  return 1;
}

/** Open the next wizard step from saved draft payload (CMS-CRC-1 GET). */
export function resolveCriminalDraftResumeStep(
  response: Pick<CriminalStepsResponse, 'step_1' | 'step_2' | 'step_3' | 'step_4' | 'step_5' | 'current_step'>,
): CriminalWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  if (!step1LooksComplete(s1)) {
    const latest = latestSavedCriminalStep(response);
    return latest > 1 ? latest : 1;
  }
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  if (!step5LooksComplete(s5)) return 5;
  return 5;
    },
    (step) => step >= 1 && step <= 5,
  );
}

/** API may return snake_case or camelCase on save / steps GET. */
export function caseRequestIdFromResponse(response: CriminalStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: CriminalStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function newAccused(): AccusedRecord {
  return { id: crypto.randomUUID(), name: '', status: '', statusOther: '' };
}

export function createEmptyCriminalDraft(): CriminalWizardDraft {
  return {
    fileCategory: FILE_CATEGORY_CRIMINAL,
    fileCategoryId: '',
    subFileCategoryId: '',
    subFileCategoryName: '',
    applicableAreas: [],
    applicableAreaIds: [],
    instituteType: 'government',
    corporateInstitute: '',
    corporateInstituteName: '',
    corporateInstituteOther: '',
    corporateInstituteContactName: '',
    corporateInstitutePrefix: '',
    description: '',
    caseSourceId: '',
    caseSource: '',
    caseSourceOther: '',
    provinceId: '',
    provinceName: '',
    districtId: '',
    districtName: '',
    highCourtId: '',
    highCourtName: '',
    magistrateCourtId: '',
    magistrateCourtName: '',
    policeStationId: '',
    policeStationName: '',
    yourReferenceNumber: '',
    otherExternalReferences: '',
    caseNumber: '',
    hasConnectedMatters: 'no',
    connectedCaseNumber: '',
    connectedAgdFileRefAvailable: false,
    connectedAgdFileRef: '',
    victimName: '',
    victimDob: '',
    victimGender: '',
    victimGenderId: '',
    offenceDateFrom: '',
    offenceDateTo: '',
    placeOfOffence: '',
    accusedDetailsAvailable: true,
    accused: [newAccused()],
    urgencyLevelId: '',
    urgencyLevelName: '',
    deadline: '',
    urgencyJustification: '',
    deliveryMethodIds: [],
    deliveryMethods: [],
    deliveryOther: '',
    documents: [],
    confirmCorrect: false,
    draftRequestId: '',
    referenceNo: '',
  };
}

function pickStr(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function accusedFromStep3(s3: CriminalSaveStep3 | undefined, prevAccused: AccusedRecord[] | undefined): AccusedRecord[] {
  const prev = Array.isArray(prevAccused) && prevAccused.length ? prevAccused : [newAccused()];
  if (!s3) return prev;

  const stepStatusId = pickStr(s3.suspect_status_id);
  const stepStatusName = pickStr(s3.suspect_status_name);

  const singleName = pickStr(s3.suspect_name);
  if (singleName) {
    return [
      {
        id: crypto.randomUUID(),
        name: singleName,
        status: stepStatusId,
        statusOther: stepStatusName,
      },
    ];
  }

  const fromSuspectNames = Array.isArray(s3.suspect_names) ? s3.suspect_names : [];
  if (fromSuspectNames.length) {
    const first = fromSuspectNames[0];
    if (first && typeof first === 'object') {
      return fromSuspectNames
        .map((item) => {
          if (typeof item === 'string') {
            return {
              id: crypto.randomUUID(),
              name: item.trim(),
              status: stepStatusId,
              statusOther: stepStatusName,
            };
          }
          const row = item as Record<string, unknown>;
          const name = pickStr(row.suspect_name, row.name);
          const statusId = pickStr(row.suspect_status_id, stepStatusId);
          return {
            id: crypto.randomUUID(),
            name,
            status: statusId,
            statusOther: stepStatusName,
          };
        })
        .filter((a) => a.name || a.status);
    }

    return fromSuspectNames
      .filter((n): n is string => typeof n === 'string' && Boolean(n.trim()))
      .map((name) => ({
        id: crypto.randomUUID(),
        name: name.trim(),
        status: stepStatusId,
        statusOther: stepStatusName,
      }));
  }

  const suspects = s3.suspects;
  if (Array.isArray(suspects) && suspects.length > 0) {
    if (typeof suspects[0] === 'string') {
      return (suspects as string[])
        .filter((n) => typeof n === 'string' && n.trim())
        .map((name) => ({
          id: crypto.randomUUID(),
          name: name.trim(),
          status: stepStatusId,
          statusOther: stepStatusName,
        }));
    }

    return (suspects as Array<Record<string, unknown> | string>)
      .map((s) => {
        if (typeof s === 'string') {
          return {
            id: crypto.randomUUID(),
            name: s.trim(),
            status: stepStatusId,
            statusOther: stepStatusName,
          };
        }
        const name = pickStr(s.suspect_name, s.name, s.accused_name, s.accused_suspect_name);
        const statusId = pickStr(s.suspect_status_id, s.status_id, stepStatusId);
        const statusName = pickStr(s.suspect_status_name, s.status_name, s.suspect_status_other);
        return {
          id: pickStr(s.id) || crypto.randomUUID(),
          name,
          status: statusId,
          statusOther: statusName,
        };
      })
      .filter((a) => a.name || a.status);
  }

  if (stepStatusId || stepStatusName) {
    const existing = prev[0];
    return [
      {
        id: existing?.id ?? crypto.randomUUID(),
        name: existing?.name ?? '',
        status: stepStatusId || existing?.status || '',
        statusOther: stepStatusName || existing?.statusOther || '',
      },
    ];
  }

  return prev;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Comma-separated applicable area labels (cover note / steps GET). */
export function parseApplicableAreaLabels(value: string | undefined | null): string[] {
  if (!value?.trim()) return [];
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

/** Steps GET may return applicable_areas as { applicable_area_id, applicable_area_name }[]. */
export function parseApplicableAreasFromApi(value: unknown): { ids: string[]; names: string[] } {
  if (typeof value === 'string') {
    const labels = parseApplicableAreaLabels(value);
    const ids = labels.filter((l) => UUID_RE.test(l));
    const names = labels.filter((l) => !UUID_RE.test(l));
    return { ids, names };
  }
  if (!Array.isArray(value) || !value.length) return { ids: [], names: [] };

  const ids: string[] = [];
  const names: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      const t = item.trim();
      if (!t) continue;
      if (UUID_RE.test(t)) ids.push(t);
      else names.push(t);
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const row = item as ApplicableAreaRow;
    const id = (row.applicable_area_id ?? row.applicableAreaId)?.trim();
    const name = (row.applicable_area_name ?? row.applicableAreaName)?.trim();
    if (id) ids.push(id);
    if (name) names.push(name);
  }
  return { ids, names };
}

function applicableAreaNamesFromField(value: string | string[] | undefined | null): string[] {
  if (typeof value === 'string') return parseApplicableAreaLabels(value);
  if (!Array.isArray(value)) return [];
  const names = value
    .filter((n): n is string => typeof n === 'string' && Boolean(n.trim()))
    .map((n) => n.trim());
  if (!names.length) return [];
  if (names.every((n) => UUID_RE.test(n))) return [];
  return names;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function applicableAreaIdsFromField(value: string[] | string | undefined): string[] {
  if (typeof value === 'string') {
    const ids = parseApplicableAreaLabels(value).filter((id) => UUID_RE.test(id));
    return ids;
  }
  if (!Array.isArray(value) || !value.length) return [];
  const ids = value
    .filter((id): id is string => typeof id === 'string' && UUID_RE.test(id.trim()))
    .map((id) => id.trim());
  return ids.length === value.length ? ids : [];
}

function resolveApplicableAreaNames(
  response: CriminalStepsResponse,
  s1: CriminalSaveStep1 | undefined,
  prev: string[],
): string[] {
  for (const src of [response.applicable_areas, s1?.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }

  const sources: (string | string[] | undefined | null)[] = [
    response.applicable_area_names,
    response.applicableAreaNames,
    s1?.applicable_area_names,
    s1?.applicableAreaNames,
    response.applicableAreas,
    s1?.applicableAreas,
    typeof response.applicable_areas === 'string' ? response.applicable_areas : undefined,
    typeof s1?.applicable_areas === 'string' ? s1.applicable_areas : undefined,
  ];
  for (const src of sources) {
    const names = applicableAreaNamesFromField(src);
    if (names.length) return names;
  }
  return prev;
}

function resolveApplicableAreaIds(
  response: CriminalStepsResponse,
  s1: CriminalSaveStep1 | undefined,
  prev: string[],
): string[] {
  for (const src of [response.applicable_areas, s1?.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }

  const sources: (string[] | string | undefined)[] = [
    isStringArray(response.applicable_areas) ? response.applicable_areas : undefined,
    response.applicableAreaIds,
    isStringArray(s1?.applicable_areas) ? s1.applicable_areas : undefined,
  ];
  for (const src of sources) {
    const ids = applicableAreaIdsFromField(src);
    if (ids.length) return ids;
  }
  return prev;
}

/** CMS-CRC-1 step_4 — API may return string[] or { delivery_method_id, delivery_method_name }[]. */
export function parsePhysicalFileDeliveryMethods(
  raw: CriminalSaveStep4['physical_file_delivery_methods'] | undefined,
): { ids: string[]; names: string[] } {
  if (!raw?.length) return { ids: [], names: [] };

  if (typeof raw[0] === 'string') {
    const names = (raw as string[]).map((s) => s.trim()).filter(Boolean);
    return { ids: [], names };
  }

  const ids: string[] = [];
  const names: string[] = [];
  for (const item of raw as PhysicalFileDeliveryMethodRow[]) {
    if (!item || typeof item !== 'object') continue;
    const id = item.delivery_method_id?.trim();
    const name = item.delivery_method_name?.trim();
    if (id) ids.push(id);
    if (name) names.push(name);
  }
  return { ids, names };
}

function mapStep1(draft: CriminalWizardDraft): SaveStepFields<CriminalSaveStep1> {
  return {
    description: strOrNull(draft.description),
    category_id: strOrNull(draft.fileCategoryId),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    case_source_id: strOrNull(draft.caseSourceId),
    case_source_description: strOrNull(draft.caseSourceOther),
    province_id: strOrNull(draft.provinceId),
    district_id: strOrNull(draft.districtId),
    high_court_id: strOrNull(draft.highCourtId),
    magistrate_court_id: strOrNull(draft.magistrateCourtId),
    police_station_id: strOrNull(draft.policeStationId),
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: draft.hasConnectedMatters === 'yes' ? true : draft.hasConnectedMatters === 'no' ? false : undefined,
    connected_case_number: draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_available:
      draft.hasConnectedMatters === 'yes' ? draft.connectedAgdFileRefAvailable : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' && draft.connectedAgdFileRefAvailable
        ? strOrNull(draft.connectedAgdFileRef)
        : undefined,
  };
}

function mapStep2(draft: CriminalWizardDraft): SaveStepFields<CriminalSaveStep2> {
  const age = calcAgeFromDob(draft.victimDob);
  return {
    name_of_victim: strOrNull(draft.victimName),
    date_of_birth: strOrNull(draft.victimDob),
    age: typeof age === 'number' ? age : undefined,
    gender_id: strOrNull(draft.victimGenderId),
  };
}

function mapStep3(draft: CriminalWizardDraft): SaveStepFields<CriminalSaveStep3> {
  const accusedAvailable = draft.accusedDetailsAvailable;
  const suspectNames = accusedAvailable
    ? draft.accused
        .filter((a) => a.name.trim())
        .map((a) => ({
          suspect_name: a.name.trim(),
          suspect_status_id: strOrNull(a.status),
        }))
    : [];
  const primaryStatus = accusedAvailable ? draft.accused.find((a) => a.status)?.status : undefined;
  return {
    date_of_offence_from: strOrNull(draft.offenceDateFrom),
    date_of_offence_to: strOrNull(draft.offenceDateTo),
    place_of_offence: strOrNull(draft.placeOfOffence),
    accused_details_available: accusedAvailable,
    suspect_status_id: accusedAvailable ? strOrNull(primaryStatus) : undefined,
    deadline: strOrNull(draft.deadline),
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    urgency_justification: strOrNull(draft.urgencyJustification),
    suspect_names: suspectNames.length ? suspectNames : undefined,
  };
}

function otherMethodFromStep4(s4: CriminalSaveStep4 | undefined): string {
  const raw = s4?.other_method ?? s4?.otherMethod;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s4: CriminalSaveStep4 | undefined): string {
  const raw = s4?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw as PhysicalFileDeliveryMethodRow[]) {
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name && isOtherLookup(name) && other) return other;
  }
  return '';
}

function mapStep4(draft: CriminalWizardDraft): SaveStepFields<CriminalSaveStep4> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  const step4: CriminalSaveStep4 = {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
  };

  if (draftHasOtherDeliverySelected(draft)) {
    step4.other_method = strOrNull(draft.deliveryOther);
  }

  return step4;
}

function originalFileNameFromStep5File(file: CriminalSaveStep5File): string | null | undefined {
  if ('original_file_name' in file) return file.original_file_name;
  return (file as { originalFileName?: string | null }).originalFileName;
}

function step5FileFields(file: CriminalSaveStep5File): { fileName: string; originalFileName: string } {
  return normalizeDocumentFileFields(file.file_name ?? '', originalFileNameFromStep5File(file));
}

/** Save payload: `file_name` = DMS key, `original_file_name` = user-facing upload label. */
function step5FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function mapStep5(draft: CriminalWizardDraft): SaveStepFields<CriminalSaveStep5> {
  const uploaded_files = draft.documents
    .filter((d) => {
      return isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason));
    })
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const typed = isPredefinedDocumentType(d.documentTypeId, d.checklistItemId);
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(typed && documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter((d) => !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)))
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

export function buildSaveCriminalPayload(
  draft: CriminalWizardDraft,
  options: {
    currentStep?: CriminalWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveCriminalStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveCriminalStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  /** Draft save must include step_1..current (API requires step_1 when case_request_id is new). */
  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    if (step >= 5) payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);
  if (step === 5) payload.step_5 = mapStep5(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapCriminalStepsToDraft(
  response: CriminalStepsResponse,
  prev: CriminalWizardDraft,
): CriminalWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  const fileCategoryId = s1?.category_id ?? prev.fileCategoryId;
  const fileCategoryName = s1?.category_name ?? prev.fileCategory;
  const subFileCategoryName = s1?.sub_category_name ?? prev.subFileCategoryName;

  const accused = accusedFromStep3(s3, prev.accused);

  const documents = (() => {
    const fromApi = [
      ...(s5?.uploaded_files ?? []),
      ...(s5?.extra_uploaded_files ?? []),
      ...(s5?.documents ?? []),
    ];
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => isDocumentTypeUuid(f.document_type_id))
        .map((f) => {
          const { fileName, originalFileName } = step5FileFields(f);
          return {
            id: crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: f.document_type_id ?? '',
            documentName: 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            fileSize: f.file_size,
            uploadTime: f.upload_time ?? '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find(
        (f) =>
          isDocumentTypeUuid(f.document_type_id) &&
          (f.document_type_id === doc.documentTypeId ||
            (!isDocumentTypeUuid(doc.documentTypeId) &&
              f.document_type_name &&
              doc.documentName &&
              f.document_type_name.trim().toLowerCase() === doc.documentName.trim().toLowerCase())),
      );
      if (!match) return doc;
      const { fileName, originalFileName } = step5FileFields(match);
      return {
        ...doc,
        documentTypeId: match.document_type_id ?? doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        fileSize: match.file_size ?? doc.fileSize,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  return {
    ...prev,
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategoryId,
    fileCategory: fileCategoryName,
    subFileCategoryName,
    description: s1?.description ?? prev.description,
    subFileCategoryId: s1?.sub_category_id ?? prev.subFileCategoryId,
    applicableAreaIds: resolveApplicableAreaIds(response, s1, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, s1, prev.applicableAreas),
    caseSourceId: s1?.case_source_id ?? prev.caseSourceId,
    caseSource: s1?.case_source_name ?? prev.caseSource,
    caseSourceOther:
      s1?.case_source_description?.trim() ||
      s1?.caseSourceDescription?.trim() ||
      prev.caseSourceOther,
    provinceId: s1?.province_id ?? prev.provinceId,
    provinceName: s1?.province_name ?? prev.provinceName,
    districtId: s1?.district_id ?? prev.districtId,
    districtName: s1?.district_name ?? prev.districtName,
    highCourtId: s1?.high_court_id ?? prev.highCourtId,
    highCourtName: s1?.high_court_name ?? prev.highCourtName,
    magistrateCourtId: s1?.magistrate_court_id ?? prev.magistrateCourtId,
    magistrateCourtName: s1?.magistrate_court_name ?? prev.magistrateCourtName,
    policeStationId: s1?.police_station_id ?? prev.policeStationId,
    policeStationName: s1?.police_station_name ?? prev.policeStationName,
    yourReferenceNumber: s1?.external_case_reference_number ?? prev.yourReferenceNumber,
    otherExternalReferences: s1?.other_external_case_reference_number ?? prev.otherExternalReferences,
    caseNumber: s1?.case_number ?? prev.caseNumber,
    ...(() => {
      const connectedCaseNumber = s1?.connected_case_number ?? prev.connectedCaseNumber;
      const connectedAgdFileRef = s1?.connected_agd_file_ref_number ?? prev.connectedAgdFileRef;
      // Transfer Case (Advice → Litigation) auto-fills the connected AGD file ref but may leave
      // has_connected_case=false from the source advice case. Force Yes so the linkage is visible
      // and not wiped on save when the radio is left as No.
      const hasConnectedData =
        Boolean(connectedCaseNumber?.trim()) || Boolean(connectedAgdFileRef?.trim());
      const hasConnectedMatters: CriminalWizardDraft['hasConnectedMatters'] = hasConnectedData
        ? 'yes'
        : s1?.has_connected_case === true
          ? 'yes'
          : s1?.has_connected_case === false
            ? 'no'
            : prev.hasConnectedMatters;
      return {
        hasConnectedMatters,
        connectedCaseNumber,
        connectedAgdFileRef,
        connectedAgdFileRefAvailable: (() => {
          if (s1?.connected_agd_file_ref_available != null) {
            return Boolean(s1.connected_agd_file_ref_available);
          }
          if (Boolean(connectedAgdFileRef?.trim())) return true;
          return prev.connectedAgdFileRefAvailable;
        })(),
      };
    })(),
    victimName: s2?.name_of_victim ?? prev.victimName,
    victimDob: s2?.date_of_birth ?? prev.victimDob,
    victimGenderId: s2?.gender_id ?? prev.victimGenderId,
    victimGender: s2?.gender_name ?? prev.victimGender,
    offenceDateFrom: s3?.date_of_offence_from ?? prev.offenceDateFrom,
    offenceDateTo: s3?.date_of_offence_to ?? prev.offenceDateTo,
    placeOfOffence: s3?.place_of_offence ?? prev.placeOfOffence,
    accusedDetailsAvailable: s3?.accused_details_available !== false,
    accused,
    urgencyLevelId: s3?.urgency_level_id ?? prev.urgencyLevelId,
    urgencyLevelName: s3?.urgency_level_name ?? prev.urgencyLevelName,
    deadline: s3?.deadline ?? prev.deadline,
    urgencyJustification: s3?.urgency_justification ?? prev.urgencyJustification,
    ...(() => {
      const delivery = parsePhysicalFileDeliveryMethods(s4?.physical_file_delivery_methods);
      const ids = s4?.physical_file_delivery_method_ids?.length
        ? s4.physical_file_delivery_method_ids
        : delivery.ids;
      return {
        deliveryMethodIds: ids.length ? ids : prev.deliveryMethodIds,
        deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
        deliveryOther: otherMethodFromStep4(s4) || otherMethodFromDeliveryRows(s4) || prev.deliveryOther,
      };
    })(),
    documents,
  };
}
